import { AppIcon } from '../ui/icon'
import { mdiCellphone, mdiEmailOutline, mdiMessageOutline } from '@mdi/js'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { $api } from '../../api/queryClient'
import { Badge } from '../ui/badge'
import { Card, CardContent } from '../ui/card'
import { EmptyState } from '../ui/empty-state'
import { Skeleton } from '../ui/skeleton'
import { displaySender, formatQueueDate } from './ConversationsPage'
import { NewConversationButton } from './NewConversationDialog'
import type { Conversation } from '../../api/schema'
import { threadTitleT } from './thread'
import { useCan } from '../../contexts/PermissionsContext'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * "Conversations" on an entity page: the latest threads of an asset, an issue
 * or a contact.
 *
 * - Asset: every thread whose scope columns include this asset — anchored
 *   here, or merely about it. This is where the questions that never became
 *   tickets are found again ("a-t-on déjà eu une infiltration côté ouest ?").
 *   Without the about-set join an `answer` outcome would leave no trace on the
 *   asset at all, and the referential would remember only the requests that
 *   happened to become work.
 * - Issue: the threads attributed to the ticket.
 * - Contact: the threads whose resolved sender is this contact. The contact is
 *   the sender of every row, so the sender line gives way to the date and the
 *   summary: what was asked and where it stands is what a manager opening a
 *   contact wants to read.
 *
 * The whole section is a conversations read: without `conversations.read` it
 * renders nothing and asks nothing, rather than raising the central
 * permission-denied notification on every page that embeds it.
 */

const CHANNEL_ICON = { email: mdiEmailOutline, whatsapp: mdiCellphone, web: mdiMessageOutline } as const

const ALL_STATUSES = 'open,waiting_resident,closed'

export function EntityConversations({
    org,
    assetUid,
    assetName,
    issueUid,
    contactUid,
    card = false,
}: {
    org: string
    assetUid?: string
    /** Shown in the "new conversation" dialog once the site is pre-anchored. */
    assetName?: string
    issueUid?: string
    /** The contact page: the threads whose resolved sender is this user. */
    contactUid?: string
    /**
     * Render as a card of its own. The issue page puts the resident side ABOVE
     * the internal comments, in its own frame, and only when there is one — so
     * the wrapper has to appear and disappear with the rows, which only this
     * component knows.
     */
    card?: boolean
}) {
    const { t, i18n } = useTranslation('conversations')
    const canRead = useCan('conversations.read')

    const { data, isLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/conversations',
        {
            params: {
                path: { orgCode: org },
                query: {
                    asset: assetUid,
                    issue: issueUid,
                    contact: contactUid,
                    status: ALL_STATUSES,
                    limit: 10,
                },
            },
        },
        { enabled: canRead },
    )

    if (!canRead) return null

    const rows = ((data as { data?: Conversation[] } | undefined)?.data ?? []) as Conversation[]

    if (contactUid) {
        const total = (data as { total?: number } | undefined)?.total ?? rows.length

        // The contact page keeps the section even with no thread: the header's
        // « Send a message » is the door, and this is where its thread lands.
        return (
            <section data-testid="contact-conversations" className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-3">
                    {t('title')}
                    {total > 0 && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground" data-testid="contact-conversations-total">
                            {total}
                        </span>
                    )}
                </h2>
                {isLoading ? (
                    <div className="space-y-2">
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        variant="inline"
                        icon={SECTION_ICONS.conversations}
                        title={t('contact_section.empty')}
                        data-testid="contact-conversations-empty"
                    />
                ) : (
                    <ul className="divide-y border rounded-lg">
                        {rows.map((row) => (
                            <li key={row.uid} data-testid={`contact-conversation-${row.uid}`}>
                                <ConversationLink org={org} uid={row.uid}>
                                    <ChannelIcon row={row} />
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-baseline gap-2 min-w-0">
                                            <span className="text-sm font-medium text-foreground truncate">
                                                {threadTitleT(row, t)}
                                            </span>
                                            {/* The date the queue sorts on, so the rows read in order. */}
                                            <span
                                                className="ml-auto text-xs text-muted-foreground whitespace-nowrap"
                                                data-testid="contact-conversation-date"
                                            >
                                                {formatQueueDate(row.last_inbound_at ?? row.created_at, i18n.language, t('queue.today'))}
                                            </span>
                                        </span>
                                        {/* `preview` is the stored summary when there is one, the
                                            first line of the latest inbound message otherwise. */}
                                        {row.preview && (
                                            <span
                                                className="text-xs text-muted-foreground line-clamp-3"
                                                data-testid="contact-conversation-summary"
                                            >
                                                {row.preview}
                                            </span>
                                        )}
                                    </span>
                                    <StatusBadge row={row} />
                                </ConversationLink>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        )
    }

    // An asset page keeps the section even with no thread: the entry point that
    // opens one is here (spec 2026-09-15-01 § B), and hiding it until someone
    // else writes first is exactly the gap this spec closes. An issue page has
    // no such door and disappears as before.
    const canStart = Boolean(assetUid) && !issueUid
    if (isLoading || (rows.length === 0 && !canStart)) {
        return null
    }

    const section = (
        <section className="space-y-2" data-testid={issueUid ? 'issue-conversations' : 'asset-conversations'}>
            <div className="flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">{t('title')}</h2>
                {canStart && (
                    <NewConversationButton
                        org={org}
                        assetUid={assetUid}
                        assetName={assetName}
                        variant="outline"
                        size="sm"
                        testId="asset-conversation-new"
                    />
                )}
            </div>
            {rows.length > 0 && (
            <ul className="divide-y border rounded-lg">
                {rows.map((row) => (
                    <li key={row.uid} data-testid={`entity-conversation-${row.uid}`}>
                        <ConversationLink org={org} uid={row.uid}>
                            <ChannelIcon row={row} />
                            <span className="min-w-0 flex-1">
                                <span className="block text-sm text-foreground truncate">
                                    {threadTitleT(row, t)}
                                </span>
                                <span className="block text-[11px] text-muted-foreground truncate">
                                    {displaySender(row, t('contact.anonymous'))}
                                </span>
                            </span>
                            <StatusBadge row={row} />
                        </ConversationLink>
                    </li>
                ))}
            </ul>
            )}
        </section>
    )

    if (!card) return section

    return (
        <Card data-testid="issue-conversations-card">
            <CardContent className="py-4">{section}</CardContent>
        </Card>
    )
}

/** A row opens the thread in the queue, every status shown. */
function ConversationLink({ org, uid, children }: { org: string; uid: string | undefined; children: ReactNode }) {
    return (
        <Link
            to="/$org/conversations"
            params={{ org }}
            search={{
                conversation: uid,
                status: ALL_STATUSES,
                channel: undefined,
                resolution: undefined,
                asset: undefined,
                unattached: undefined,
                draft: undefined,
                q: undefined,
                selected: undefined,
                reply_for_issue: undefined,
                message: undefined,
            }}
            className="flex items-start gap-2 px-3 py-2 hover:bg-muted/50"
        >
            {children}
        </Link>
    )
}

function ChannelIcon({ row }: { row: Conversation }) {
    const { t } = useTranslation('conversations')
    const channel = row.channel ?? 'email'
    const path = CHANNEL_ICON[channel as keyof typeof CHANNEL_ICON] ?? mdiEmailOutline

    return (
        <AppIcon
            path={path}
            size="xs"
            className="mt-0.5 flex-shrink-0 text-muted-foreground"
            aria-label={t(`channel.${channel}`, { defaultValue: channel })}
        />
    )
}

function StatusBadge({ row }: { row: Conversation }) {
    const { t } = useTranslation('conversations')

    return (
        <Badge variant="outline" className="text-[10px] flex-shrink-0">
            {t(`status.${row.status ?? 'open'}`, { defaultValue: String(row.status ?? '') })}
        </Badge>
    )
}
